import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * BulkPropertyAssignment Component
 * 
 * Allows administrators to assign properties to multiple users at once with modern UI/UX.
 * Features:
 * - Dual-panel selection interface
 * - Real-time search filtering
 * - Batch selection controls
 * - Visual assignment summary
 * - Enhanced accessibility
 */
const BulkPropertyAssignment = () => {
    const { apiFetch, user } = useAuth();
    
    // State
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [propertyGroups, setPropertyGroups] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [selectedPropertyGroups, setSelectedPropertyGroups] = useState([]);
    const [replaceExisting, setReplaceExisting] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [searchUsers, setSearchUsers] = useState('');
    const [searchProperties, setSearchProperties] = useState('');

    useEffect(() => {
        const loadData = async () => {
            setIsInitialLoading(true);
            await Promise.all([
                fetchUsers(),
                fetchProperties(),
                fetchPropertyGroups()
            ]);
            setIsInitialLoading(false);
        };
        loadData();
    }, []);

    /**
     * Fetches all active users.
     */
    const fetchUsers = async () => {
        try {
            const response = await apiFetch('/api/Permissions/active-users');
            const data = await response.json();
            if (data.success) {
                setUsers(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        }
    };

    /**
     * Fetches all properties.
     */
    const fetchProperties = async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success) {
                setProperties(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching properties:', error);
            toast.error('Failed to load properties');
        }
    };

    /**
     * Fetches all property groups.
     */
    const fetchPropertyGroups = async () => {
        try {
            const response = await apiFetch('/api/Property/groups/all');
            const data = await response.json();
            if (data.success) {
                setPropertyGroups(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching property groups:', error);
            toast.error('Failed to load property groups');
        }
    };

    /**
     * Handles bulk property assignment with validation.
     */
    const handleBulkAssign = async () => {
        if (selectedUsers.length === 0) {
            toast.error('Please select at least one user');
            return;
        }

        if (selectedProperties.length === 0 && selectedPropertyGroups.length === 0) {
            toast.error('Please select at least one property or property group');
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiFetch('/api/Permissions/bulk-assign-properties', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userIDs: selectedUsers,
                    propertyIDs: selectedProperties,
                    propertyGroupIDs: selectedPropertyGroups.length > 0 ? selectedPropertyGroups : null,
                    replaceExisting: replaceExisting,
                    modifiedByUserID: user?.UserID || 0
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success(data.message || 'Properties assigned successfully');
                // Reset selections
                setSelectedUsers([]);
                setSelectedProperties([]);
                setSelectedPropertyGroups([]);
            } else {
                toast.error(data.message || 'Failed to bulk assign properties');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error bulk assigning:', error);
            toast.error('Failed to bulk assign properties');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Toggles user selection.
     */
    const toggleUser = (userID) => {
        if (selectedUsers.includes(userID)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userID));
        } else {
            setSelectedUsers([...selectedUsers, userID]);
        }
    };

    /**
     * Toggles property selection.
     */
    const toggleProperty = (propertyID) => {
        if (selectedProperties.includes(propertyID)) {
            setSelectedProperties(selectedProperties.filter(id => id !== propertyID));
        } else {
            setSelectedProperties([...selectedProperties, propertyID]);
        }
    };

    /**
     * Toggles property group selection.
     */
    const togglePropertyGroup = (groupID) => {
        if (selectedPropertyGroups.includes(groupID)) {
            setSelectedPropertyGroups(selectedPropertyGroups.filter(id => id !== groupID));
        } else {
            setSelectedPropertyGroups([...selectedPropertyGroups, groupID]);
        }
    };

    /**
     * Selects all filtered users.
     */
    const selectAllUsers = () => {
        const filtered = filteredUsers.map(u => u.userID);
        setSelectedUsers(filtered);
    };

    /**
     * Clears all selected users.
     */
    const clearAllUsers = () => {
        setSelectedUsers([]);
    };

    /**
     * Selects all filtered properties.
     */
    const selectAllProperties = () => {
        const filtered = filteredProperties.map(p => p.propertyID);
        setSelectedProperties(filtered);
    };

    /**
     * Clears all selected properties.
     */
    const clearAllProperties = () => {
        setSelectedProperties([]);
    };

    const filteredUsers = users.filter(u =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchUsers.toLowerCase())
    );

    const filteredProperties = properties.filter(p =>
        (p.propertyName && p.propertyName.toLowerCase().includes(searchProperties.toLowerCase())) ||
        (p.comparativeName && p.comparativeName.toLowerCase().includes(searchProperties.toLowerCase()))
    );
    return (
        <div className="bulk-assignment-container-modern">
{/* Header */}
            <div className="properties-header">
                <h1>Bulk Property Assignment</h1>
                <p style={{ margin: '8px 0 0 0', color: '#718096', fontSize: '0.95rem' }}>
                    Assign properties and property groups to multiple users at once
                </p>
            </div>

            {isInitialLoading ? (
                <div className="loading-state">
                    <div className="spinner" aria-label="Loading data"></div>
                    <p>Loading bulk assignment data...</p>
                </div>
            ) : (
                <>
                    {/* Selection Panels Grid */}
                    <div className="bulk-assignment-grid-modern">
                        {/* Users Selection Panel */}
                        <div className="selection-panel-modern">
                            <div className="panel-header-modern">
                                <h3>👥 Select Users</h3>
                                <span className="selection-count">{selectedUsers.length} selected</span>
                            </div>
                            
                            <div className="panel-actions-modern">
                                <button onClick={selectAllUsers} className="btn-secondary btn-sm">
                                    Select All ({filteredUsers.length})
                                </button>
                                <button onClick={clearAllUsers} className="btn-secondary btn-sm" disabled={selectedUsers.length === 0}>
                                    Clear
                                </button>
                            </div>
                            
                            <div className="search-container" style={{ marginTop: '12px' }}>
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search users..."
                                    value={searchUsers}
                                    onChange={(e) => setSearchUsers(e.target.value)}
                                    aria-label="Search users"
                                />
                            </div>
                            
                            <div className="checkbox-list-modern">
                                {filteredUsers.length === 0 ? (
                                    <div className="empty-list">
                                        <p>No users found</p>
                                    </div>
                                ) : (
                                    filteredUsers.map((u) => (
                                        <label key={u.userID} className="checkbox-item-modern">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(u.userID)}
                                                onChange={() => toggleUser(u.userID)}
                                                aria-label={`Select ${u.firstName} ${u.lastName}`}
                                            />
                                            <span className="checkbox-label">
                                                {u.firstName} {u.lastName}
                                            </span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Properties Selection Panel */}
                        <div className="selection-panel-modern">
                            <div className="panel-header-modern">
                                <h3>🏢 Select Properties/Groups</h3>
                                <span className="selection-count">
                                    {selectedProperties.length + selectedPropertyGroups.length} selected
                                </span>
                            </div>

                            {/* Property Groups Section */}
                            <div className="section-modern">
                                <h4>Property Groups ({selectedPropertyGroups.length} selected)</h4>
                                <div className="checkbox-list-modern checkbox-list-short">
                                    {propertyGroups.length === 0 ? (
                                        <div className="empty-list">
                                            <p>No property groups available</p>
                                        </div>
                                    ) : (
                                        propertyGroups.map((g) => (
                                            <label key={g.propertyGroupID} className="checkbox-item-modern">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPropertyGroups.includes(g.propertyGroupID)}
                                                    onChange={() => togglePropertyGroup(g.propertyGroupID)}
                                                    aria-label={`Select group ${g.groupName}`}
                                                />
                                                <span className="checkbox-label">
                                                    {g.groupName} 
                                                    <span className="item-count">({g.propertyCount} properties)</span>
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Individual Properties Section */}
                            <div className="section-modern section-with-spacing">
                                <div className="section-header-with-actions">
                                    <h4>Individual Properties ({selectedProperties.length} selected)</h4>
                                    <div className="section-actions">
                                        <button onClick={selectAllProperties} className="btn-secondary btn-sm">
                                            Select All ({filteredProperties.length})
                                        </button>
                                        <button onClick={clearAllProperties} className="btn-secondary btn-sm" disabled={selectedProperties.length === 0}>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="search-container">
                                    <span className="search-icon">🔍</span>
                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Search properties..."
                                        value={searchProperties}
                                        onChange={(e) => setSearchProperties(e.target.value)}
                                        aria-label="Search properties"
                                    />
                                </div>
                                
                                <div className="checkbox-list-modern">
                                    {filteredProperties.length === 0 ? (
                                        <div className="empty-list">
                                            <p>No properties found</p>
                                        </div>
                                    ) : (
                                        filteredProperties.map((p) => (
                                            <label key={p.propertyID} className="checkbox-item-modern">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProperties.includes(p.propertyID)}
                                                    onChange={() => toggleProperty(p.propertyID)}
                                                    aria-label={`Select property ${p.propertyName}`}
                                                />
                                                <span className="checkbox-label">
                                                    {p.propertyName}
                                                    {p.comparativeName && (
                                                        <span className="item-detail"> ({p.comparativeName})</span>
                                                    )}
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Assignment Options */}
                    <div className="assignment-options-modern">
                        <label className="option-label-modern">
                            <input
                                type="checkbox"
                                checked={replaceExisting}
                                onChange={(e) => setReplaceExisting(e.target.checked)}
                                aria-label="Replace existing property assignments"
                            />
                            <div>
                                <span className="option-text">Replace existing property assignments</span>
                                <p className="help-text-modern">
                                    {replaceExisting 
                                        ? '⚠️ Selected properties will replace all existing property assignments for the selected users.'
                                        : '✅ Selected properties will be added to existing property assignments for the selected users.'}
                                </p>
                            </div>
                        </label>
                    </div>

                    {/* Summary and Action */}
                    <div className="bulk-assignment-summary-modern">
                        <div className="summary-stats-modern">
                            <div className="stat-modern">
                                <span className="stat-value">{selectedUsers.length}</span>
                                <span className="stat-label">Users</span>
                            </div>
                            <div className="stat-divider">×</div>
                            <div className="stat-modern">
                                <span className="stat-value">{selectedProperties.length}</span>
                                <span className="stat-label">Properties</span>
                            </div>
                            <div className="stat-divider">+</div>
                            <div className="stat-modern">
                                <span className="stat-value">{selectedPropertyGroups.length}</span>
                                <span className="stat-label">Groups</span>
                            </div>
                        </div>
                        <button
                            onClick={handleBulkAssign}
                            disabled={isLoading || selectedUsers.length === 0 || (selectedProperties.length === 0 && selectedPropertyGroups.length === 0)}
                            className="btn-primary btn-large-action"
                        >
                            {isLoading ? '⏳ Assigning...' : '✨ Bulk Assign Properties'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default BulkPropertyAssignment;
